import React from "react";
import { motion } from "framer-motion";
import PicImg from "../PicImg";

/**
 * Optional Accessories — 8 (or N) premium cards on matte-black bg with
 * optional image thumbnail area. If item has `image`, renders the photo;
 * if only `Icon`, renders a beautiful icon fallback with grid + radial glow.
 *
 * Props:
 *  - testId
 *  - eyebrow (default "Optional Accessories")
 *  - heading (ReactNode)
 *  - subtitle
 *  - items: { Icon, image?, title, desc }[]
 *  - gridCols: string (default "lg:grid-cols-4")
 */
export default function OptionalAccessories({
  testId,
  eyebrow = "Optional Accessories",
  heading,
  subtitle,
  items = [],
  gridCols = "lg:grid-cols-4",
}) {
  return (
    <section
      data-testid={testId}
      className="relative bg-liftile-matte-black py-24 md:py-32 overflow-hidden"
    >
      <div
        aria-hidden
        className="absolute top-0 left-0 right-0 h-px bg-liftile-orange/30"
      />
      <div
        aria-hidden
        className="absolute -bottom-32 -right-32 w-[520px] h-[520px] rounded-full pointer-events-none opacity-[0.12]"
        style={{
          background:
            "radial-gradient(circle, rgba(255,107,26,0.55) 0%, rgba(255,107,26,0) 70%)",
          filter: "blur(60px)",
        }}
      />

      <div className="container-x relative">
        <motion.div
          initial={{ opacity: 0, y: 22 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.65 }}
          className="max-w-3xl mb-14 md:mb-16"
        >
          <div className="heading-eyebrow mb-3">{eyebrow}</div>
          <h2 className="display-2 text-liftile-cream">{heading}</h2>
          {subtitle && (
            <p className="font-body text-base md:text-lg text-liftile-cream/70 mt-6 leading-relaxed">
              {subtitle}
            </p>
          )}
        </motion.div>

        <div
          className={`grid grid-cols-1 sm:grid-cols-2 ${gridCols} gap-6 md:gap-7`}
        >
          {items.map((a, i) => (
            <motion.article
              key={a.title}
              data-testid={`${testId}-card-${i}`}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.15 }}
              transition={{
                duration: 0.55,
                delay: i * 0.05,
                ease: [0.22, 1, 0.36, 1],
              }}
              className="group relative h-full flex flex-col rounded-2xl bg-white/[0.03] border border-white/[0.08] overflow-hidden hover:border-liftile-orange/50 hover:bg-white/[0.05] hover:-translate-y-1 transition-all duration-500"
            >
              <div className="relative aspect-[4/3] bg-gradient-to-br from-black to-liftile-matte-black overflow-hidden">
                {a.image ? (
                  <PicImg
                    src={a.image}
                    alt={a.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" loading="lazy" decoding="async" />
                ) : (
                  <div className="w-full h-full grid place-items-center">
                    <div
                      aria-hidden
                      className="absolute inset-0 opacity-[0.06] pointer-events-none"
                      style={{
                        backgroundImage:
                          "linear-gradient(rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.5) 1px, transparent 1px)",
                        backgroundSize: "36px 36px",
                      }}
                    />
                    <div
                      aria-hidden
                      className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-40 h-40 rounded-full opacity-30 pointer-events-none"
                      style={{
                        background:
                          "radial-gradient(circle, rgba(255,107,26,0.55) 0%, rgba(255,107,26,0) 70%)",
                        filter: "blur(30px)",
                      }}
                    />
                    <div className="relative w-16 h-16 rounded-2xl bg-liftile-orange/15 border border-liftile-orange/40 flex items-center justify-center group-hover:bg-liftile-orange group-hover:border-liftile-orange transition-colors duration-500">
                      <a.Icon
                        className="w-7 h-7 text-liftile-orange group-hover:text-white transition-colors duration-500"
                        strokeWidth={1.5}
                      />
                    </div>
                  </div>
                )}
                <span className="absolute top-0 right-0 h-[2px] w-0 bg-liftile-orange transition-all duration-500 group-hover:w-16 rounded-bl-full" />
              </div>

              <div className="p-6 flex-1 flex flex-col">
                <h3 className="font-heading text-sm md:text-[15px] uppercase tracking-wider font-semibold text-liftile-cream leading-snug">
                  {a.title}
                </h3>
                <p className="font-body text-[13.5px] text-liftile-cream/65 mt-3 leading-relaxed flex-1">
                  {a.desc}
                </p>
              </div>
            </motion.article>
          ))}
        </div>
      </div>
    </section>
  );
}
